import { ContentType } from '@canvas/operational-types';

/**
 * The type of app running on the canvas platform.
 */
export type AppType = 'whiteboards' | 'slides';

export type AppProduct = 'confluence' | 'canvas';

export type AppObjectType = 'slide' | 'whiteboard';

const QUERY_PARAM_SLIDES = 'slides';

/**
 * A URL path segment pattern that indicates slides content.
 *
 * Matches paths like `/canvas-embed/slide/…`, `/canvas/slide/…`, or
 * `/canvas/v/123/slide/…`. The pattern looks for `/slide` as a path segment
 * (i.e. preceded by `/` and followed by `/` or end-of-path).
 */
const SLIDE_PATH_PATTERN = /\/slide(?:\/|$)/;

let resolvedAppType: AppType | undefined;

/**
 * Determines whether the current page is running in slides mode.
 *
 * Slides mode is detected by **either**:
 * 1. The presence of the `?slides` query parameter (legacy — set by the
 *    Confluence shell when the slides experiment is enabled), **or**
 * 2. The URL path containing a `/slide/` segment (new — used when the
 *    routing monolith sends traffic to `/canvas-embed/slide/…` routes).
 *
 * Both detection methods will coexist during the migration from query-param
 * based routing to path-based routing in AFM. Once the AFM routing changes
 * are live and stable, the query-param detection can be removed.
 */
export const isSlides = (url: URL): boolean =>
  url.searchParams.has(QUERY_PARAM_SLIDES) || SLIDE_PATH_PATTERN.test(url.pathname);

/**
 * Returns the current app type based on the URL.
 *
 * The result is computed once on first call and cached for the lifetime of the
 * page. See {@link isSlides} for how the app type is determined.
 */
export const getAppType = (): AppType => {
  if (resolvedAppType === undefined) {
    const href = globalThis.location?.href ?? 'ws://.';
    const url = new URL(href);
    resolvedAppType = isSlides(url) ? 'slides' : 'whiteboards';
  }

  return resolvedAppType;
};

export const getAppProduct = (): AppProduct =>
  getAppType() === 'slides' ? 'confluence' : 'canvas';

/**
 * Server-safe variant of {@link getAppProduct} that determines the product
 * from a content type literal rather than from the browser URL. Use this on
 * backend code paths (koa middleware, lambdas, knative handlers) where
 * `globalThis.location` is unavailable / not meaningful.
 *
 * Mirrors the slides -> confluence, otherwise -> canvas mapping.
 */
export const getAppProductForContentType = (contentType: string): AppProduct =>
  contentType === 'slide' ? 'confluence' : 'canvas';

export const getAppObjectType = (): AppObjectType =>
  getAppType() === 'slides' ? 'slide' : 'whiteboard';

/**
 * Exhaustive mapping of app object types to content types to provide a common function for
 * operational logic. Don't put a default case here - addition of a new app object type
 * intentionally forces compilation to fail unless this function is appropriately updated.
 */
export const getContentTypeFromAppObjectType = (
  appObjectType: AppObjectType,
): ContentType => {
  switch (appObjectType) {
    case 'whiteboard':
      return ContentType.WHITEBOARD;
    case 'slide':
      return ContentType.SLIDE;
  }
};

/**
 * Resets the cached app type. Intended for testing only.
 */
export const resetAppType = (): void => {
  resolvedAppType = undefined;
};
